import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { filter, map, take } from 'rxjs/operators';
import {
  BaseCoreState,
  baseInquiryActions,
  baseInquirySelectors,
  BaseInquiryService,
  BaseInquiryType,
  InquiryResponse,
} from 'base';
import { BaseCustomerInquiryListItemType } from './_interfaces/base-inquiry.type';
import { INQUIRY_ACTIONS_MAP } from './_const/base-customer-inquiry-actions-map.const';

@Injectable({ providedIn: 'root' })
export class BaseCustomerInquiryService {
  constructor(
    private store: Store<BaseCoreState>,
    public baseInquiryService: BaseInquiryService,
    private readonly router: Router
  ) {}

  getInquiry(inquiryId: number) {
    this.store.dispatch(baseInquiryActions.loadCustomerInquiry({ payload: inquiryId }));
    return this.store.select(baseInquirySelectors.getCustomerInquiry).pipe(
      filter((state) => !!state.ok),
      map((state) => state.data as InquiryResponse),
      take(1)
    );
  }

  openShiftViewPage(inquiry: InquiryResponse, inquiryId: number, isCopyInquiry?: boolean) {
    this.createDataForShiftView(inquiry, isCopyInquiry ? inquiryId : undefined);

    if (isCopyInquiry) {
      this.router.navigate(['inquiries/shift-plan']);
    } else {
      this.router.navigate(['inquiries', 'shift-plan', inquiryId]);
    }
  }

  createDataForShiftView(inquiry: InquiryResponse, copiedInquiryId?: number) {
    if (inquiry.dates) {
      this.store.dispatch(
        baseInquiryActions.openInquiryInEditMode({
          payload: {
            inquiry,
            similarInquiriesPayload: {
              department: inquiry.customerDepartment?.id as number,
              startDate: inquiry.dateFrom,
              endDate: inquiry.dateTo,
              shifts: inquiry.shiftTimes?.map((shift) => shift.type).join(),
              qualifications: inquiry.qualifications?.map((item) => item.id as number).join(),
            },
            copiedInquiryId,
          },
        })
      );
    }
  }

  defineIsShiftTypesMixed(inquiry: InquiryResponse) {
    if (inquiry.dates?.length) {
      if (inquiry.dates?.length === 1) {
        return false;
      }

      let isShiftsMixed = false;

      for (let index = 1; index < inquiry.dates.length; index++) {
        const item = inquiry.dates[index];
        isShiftsMixed = !this.baseInquiryService.isTwoDatesAreSimilar(item, inquiry.dates[index - 1]);
        if (isShiftsMixed) {
          break;
        }
      }

      return isShiftsMixed;
    }

    return false;
  }

  setInquiryActions(
    inquiry: BaseCustomerInquiryListItemType | BaseInquiryType,
    canPublishInquiry: boolean,
    hasCloseInquiryPermission: boolean
  ): string[] {
    const permissionKey = canPublishInquiry ? 'canPublish' : hasCloseInquiryPermission ? 'canClose' : null;

    return !!permissionKey ? INQUIRY_ACTIONS_MAP[permissionKey][inquiry.status] ?? [] : [];
  }
}
